import "./App.css";
import { firebaseConfig } from "./common/firebaseConfig";

import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getDatabase } from "firebase/database";
import { useEffect, useState } from "react";
import { APP_ROUTES, URLS, darkTheme, lightTheme } from "./common/constants";
import { Home } from "./Home";
import { Route, Routes, useLocation } from "react-router-dom";
import Login from "./Login";
import { dataHolder } from "./common/dataHolder";
import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./locale/en.json";
import ta from "./locale/ta.json";
import UpdateSchedule from "./UpdateSchedule";
import { getLanguageCode, getTheme } from "./common/storage";
import {
  CssBaseline,
  Slide,
  Snackbar,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import Header from "./Header";
import Notifier from "./common/Notifier";
import ContactUs from "./ContactUs";
import { initAuth, subscribeToAuthChanges } from "./common/auth";
import { persistDeviceId } from "./common/util";
import LiveCount from "./LiveCount";

i18next.use(initReactI18next).init({
  resources: {
    en: {
      translation: en,
    },
    ta: {
      translation: ta,
    },
    lng: "en",
    interpolation: {
      escapeValue: false,
    },
  },
});

i18next.changeLanguage(getLanguageCode() || "en");

// Initialize Firebase once
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);
const db = getDatabase(app);
const auth = initAuth(app);

if (!dataHolder.db) {
  dataHolder.db = db;
}

function App() {
  const [mode, setMode] = useState(getTheme());
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const postAnalytics = async (deviceId) => {
      if (!deviceId) return;
      try {
        await fetch(URLS.analyticsUsers, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ deviceId }),
        });
      } catch (error) {
        console.error("Analytics request failed", error);
      }
    };

    const deviceId = persistDeviceId();
    postAnalytics(deviceId);
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges((currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const destroy = Notifier.subscribe("snackbar", (data) => {
      setSnackbarData({ ...data, show: true });
    });
    return () => {
      destroy();
    };
  }, []);

  const changeTheme = () => {
    const newMode = mode === "light" ? "dark" : "light";
    setMode(newMode);
    localStorage.setItem("theme", newMode);
  };

  const [snackbarData, setSnackbarData] = useState({
    show: false,
    content: "Content",
  });

  const handleSnackbarClose = () => {
    setSnackbarData({ ...snackbarData, show: false });
  };

  const location = useLocation();
  const isHome = location.pathname === APP_ROUTES.home;

  const transition = (props) => {
    return <Slide {...props} direction="down" />;
  };

  return (
    <ThemeProvider
      theme={createTheme({
        palette: {
          mode,
          ...(mode === "light" ? lightTheme : darkTheme),
        },
      })}
    >
      <CssBaseline />
      <div>
        <Snackbar
          open={snackbarData.show}
          onClose={handleSnackbarClose}
          TransitionComponent={transition}
          message={snackbarData.content}
          key={transition.name}
          autoHideDuration={5000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        />
        <Header changeTheme={changeTheme} />
        <Routes>
          <Route path={APP_ROUTES.home} element={<Home />} />
          <Route
            path={APP_ROUTES.login}
            element={<Login user={user} authLoading={authLoading} />}
          />
          <Route
            path={APP_ROUTES.updateSchedule}
            element={<UpdateSchedule />}
          />
          <Route path={APP_ROUTES.contactUs} element={<ContactUs />} />
        </Routes>
        <LiveCount sx={{ mb: isHome ? 10 : 0 }} />
      </div>
    </ThemeProvider>
  );
}

export default App;
